import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaCoins, FaRocket, FaBriefcase, FaFileAlt, FaUsers, FaChartLine } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

interface PricingCalculatorProps {
  isOpen: boolean;
  onClose: () => void;
}

const PricingCalculator: React.FC<PricingCalculatorProps> = ({ isOpen, onClose }) => {
  const [usageAmounts, setUsageAmounts] = useState<{[key: string]: number}>({});
  const navigate = useNavigate();
  // Pay-as-you-go pricing structure (same as in main pricing page)
  const payAsYouGoPricing = {
    interviewSession: { credits: 5, price: 0.50, description: 'AI-powered interview practice session' },
    jobAnalysis: { credits: 3, price: 0.30, description: 'Smart job description analysis' },
    cvGeneration: { credits: 8, price: 0.80, description: 'ATS-optimized CV generation' },
    companyResearch: { credits: 2, price: 0.20, description: 'Company insights and research' },
    skillsGapAnalysis: { credits: 4, price: 0.40, description: 'Personalized skills assessment' }
  };

  const features = [
    {
      id: 'interviewSession',
      name: 'Interview Practice',
      icon: <FaRocket className="text-blue-400" />,
      description: 'AI-powered interview sessions with real-time feedback',
      credits: payAsYouGoPricing.interviewSession.credits,
      price: payAsYouGoPricing.interviewSession.price
    },
    {
      id: 'jobAnalysis',
      name: 'Job Analysis',
      icon: <FaBriefcase className="text-green-400" />,
      description: 'Smart job description analysis and preparation strategies',
      credits: payAsYouGoPricing.jobAnalysis.credits,
      price: payAsYouGoPricing.jobAnalysis.price
    },
    {
      id: 'cvGeneration',
      name: 'CV Generation',
      icon: <FaFileAlt className="text-purple-400" />,
      description: 'ATS-optimized CV generation and optimization',
      credits: payAsYouGoPricing.cvGeneration.credits,
      price: payAsYouGoPricing.cvGeneration.price
    },
    {
      id: 'companyResearch',
      name: 'Company Research',
      icon: <FaUsers className="text-orange-400" />,
      description: 'Company insights and market research',
      credits: payAsYouGoPricing.companyResearch.credits,
      price: payAsYouGoPricing.companyResearch.price
    },
    {
      id: 'skillsGapAnalysis',
      name: 'Skills Gap Analysis',
      icon: <FaChartLine className="text-pink-400" />,
      description: 'Personalized skills assessment and recommendations',
      credits: payAsYouGoPricing.skillsGapAnalysis.credits,
      price: payAsYouGoPricing.skillsGapAnalysis.price
    }
  ];

  const calculateTotalCredits = () => {
    return Object.entries(usageAmounts).reduce((total, [featureId, amount]) => {
      if (amount <= 0) return total; // Skip zero amounts
      const feature = features.find(f => f.id === featureId);
      if (!feature) {
        console.warn(`Feature not found: ${featureId}`);
        return total;
      }
      return total + (feature.credits * amount);
    }, 0);
  };

  const calculateTotalPrice = () => {
    return Object.entries(usageAmounts).reduce((total, [featureId, amount]) => {
      if (amount <= 0) return total; // Skip zero amounts
      const feature = features.find(f => f.id === featureId);
      if (!feature) {
        console.warn(`Feature not found: ${featureId}`);
        return total;
      }
      return total + (feature.price * amount);
    }, 0);
  };

  const updateUsageAmount = (featureId: string, amount: number) => {
    // Validate input: ensure it's a positive integer with reasonable limits
    const validatedAmount = Math.max(0, Math.min(999, Math.round(amount)));
    
    setUsageAmounts(prev => ({
      ...prev,
      [featureId]: validatedAmount
    }));
  };

  const handleInputChange = (featureId: string, value: string) => {
    const numericValue = parseInt(value) || 0;
    updateUsageAmount(featureId, numericValue);
  };

  const getRecommendedCreditPackage = () => {
    const totalCredits = calculateTotalCredits();
    if (totalCredits === 0) return null;
    
    // Use the same credit packages as in the main pricing page
    const packages = [
      { credits: 50, price: 4.99, bonus: 0 },
      { credits: 100, price: 9.99, bonus: 0 },
      { credits: 250, price: 19.99, bonus: 25 },
      { credits: 500, price: 39.99, bonus: 100 },
      { credits: 1000, price: 79.99, bonus: 300 }
    ];

    // Find the smallest package that covers the usage
    const recommended = packages.find(pkg => (pkg.credits + pkg.bonus) >= totalCredits);
    
    // If no package covers the usage, recommend the largest package
    if (!recommended) {
      return packages[packages.length - 1];
    }
    
    return recommended;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Modal */}
          <div className="flex min-h-full items-center justify-center p-4">
            <motion.div
              className="relative bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 20, stiffness: 300 }}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 rounded-t-3xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <FaCoins className="text-3xl text-white mr-4" />
                    <div>
                      <h2 className="text-2xl font-bold text-white">Pay-as-You-Go Calculator</h2>
                      <p className="text-purple-100 mt-2">Calculate your usage and find the perfect credit package</p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                  >
                    <FaTimes className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="grid lg:grid-cols-2 gap-8">
                  {/* Usage Configuration Panel */}
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold mb-4 text-gray-800">Estimate Your Monthly Usage</h3>
                      <p className="text-gray-600 mb-6">Enter how many times you plan to use each feature per month</p>
                      
                      <div className="space-y-4">
                        {features.map((feature) => (
                          <motion.div
                            key={feature.id}
                            className="p-4 rounded-xl border border-gray-200 hover:border-purple-300 transition-all duration-300"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center">
                                <div className="mr-3">
                                  {feature.icon}
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-800">{feature.name}</h4>
                                  <p className="text-sm text-gray-600">{feature.description}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-gray-600">
                                  {feature.credits} credits each
                                </div>
                                <div className="text-sm font-semibold text-purple-600">
                                  ${feature.price} each
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-4">
                              <label className="text-sm text-gray-700">Times per month:</label>
                              <input
                                type="number"
                                min="0"
                                max="999"
                                value={usageAmounts[feature.id] || 0}
                                onChange={(e) => handleInputChange(feature.id, e.target.value)}
                                className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-purple-500 focus:border-transparent text-black"
                                placeholder="0"
                              />
                              <div className="text-sm text-gray-600">
                                = {((usageAmounts[feature.id] || 0) * feature.credits)} credits
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Results Panel */}
                  <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6">
                    <h3 className="text-xl font-semibold mb-4 text-gray-800">Your Usage Summary</h3>
                    
                    <motion.div
                      className="text-center mb-6"
                      key={calculateTotalCredits()}
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="text-4xl font-bold text-purple-600 mb-2">
                        {calculateTotalCredits()} credits
                      </div>
                      <div className="text-lg text-gray-600 mb-2">
                        Total cost: ${calculateTotalPrice().toFixed(2)}
                      </div>
                      <div className="text-sm text-gray-500">
                        per month
                      </div>
                      {calculateTotalCredits() > 0 && (
                        <div className="mt-2 text-xs text-green-600">
                          ✓ Valid usage estimate
                        </div>
                      )}
                    </motion.div>

                    {/* Recommended Credit Package */}
                    {getRecommendedCreditPackage() && (
                      <div className="bg-white rounded-xl p-4 mb-6">
                        <h4 className="font-semibold text-gray-800 mb-3">Recommended Credit Package</h4>
                        <div className="text-2xl font-bold text-purple-600 mb-2">
                          {(getRecommendedCreditPackage()?.credits || 0) + (getRecommendedCreditPackage()?.bonus || 0)} credits
                        </div>
                        <div className="text-lg text-gray-600 mb-2">
                          ${getRecommendedCreditPackage()?.price}
                        </div>
                        {getRecommendedCreditPackage()?.bonus && getRecommendedCreditPackage()?.bonus! > 0 && (
                          <div className="text-sm text-green-600 mb-2">
                            +{getRecommendedCreditPackage()?.bonus} bonus credits included!
                          </div>
                        )}
                        <p className="text-sm text-gray-600">
                          This package covers your estimated usage with {Math.max(0, ((getRecommendedCreditPackage()?.credits || 0) + (getRecommendedCreditPackage()?.bonus || 0)) - calculateTotalCredits())} credits to spare
                        </p>
                      </div>
                    )}

                    {/* Usage Breakdown */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-gray-800">Usage Breakdown</h4>
                      {Object.entries(usageAmounts)
                        .filter(([_, amount]) => amount > 0)
                        .map(([featureId, amount]) => {
                          const feature = features.find(f => f.id === featureId);
                          if (!feature) {
                            console.warn(`Feature not found for breakdown: ${featureId}`);
                            return null;
                          }
                          return (
                            <div key={featureId} className="flex items-center justify-between bg-white rounded-lg p-3">
                              <div className="flex items-center">
                                {feature.icon}
                                <span className="ml-2 font-medium text-gray-800">{feature.name}</span>
                              </div>
                              <div className="text-right">
                                <div className="text-sm text-gray-600">
                                  {amount} × {feature.credits} credits
                                </div>
                                <div className="font-bold text-purple-600">
                                  ${(amount * feature.price).toFixed(2)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      
                      {calculateTotalCredits() === 0 && (
                        <p className="text-gray-500 italic text-center py-4">No usage estimated yet</p>
                      )}
                    </div>

                    <motion.button
                      className="w-full mt-6 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-lg transition-all duration-300"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate('/billing')}
                    >
                      Buy Recommended Package
                    </motion.button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PricingCalculator;